import React, { useEffect, useRef, useState } from "react";

const MIN_FR = 0.35; // минимальная ширина колонки (в fr) при ресайзе

function load(storageKey: string, defaults: Record<string, number>): Record<string, number> {
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed = raw ? (JSON.parse(raw) as Record<string, number>) : null;
    if (parsed && typeof parsed === "object") return { ...defaults, ...parsed };
  } catch {
    /* ignore */
  }
  return defaults;
}

/**
 * Ресайз колонок грида через CSS-переменные — БЕЗ ре-рендера React мид-драга.
 *
 * Ширины ключуются ПО ID КОЛОНКИ, а не по индексу: колонки можно прятать и переставлять, и
 * позиционное хранение при первом же таком изменении раздавало бы чужие ширины (а при смене
 * их числа — молча откатывалось к дефолтам). Неизвестные id из localStorage игнорируются,
 * недостающие берутся из `defaults`, поэтому добавление колонки не сбрасывает настройку.
 *
 * `gridTemplate` строится по ВИДИМЫМ колонкам (`var(--col-<id>)`), значения живут CSS-переменными
 * на общем предке (`gridRef` — обёртка над шапкой/ИТОГО/строками). Драг пишет переменные ПРЯМО в
 * DOM, браузер перекладывает грид, React не трогается; в state+localStorage коммитим на `mouseup`.
 * Соседи двигаются с нулевой суммой → грид всегда во всю ширину.
 */
export function useColumnResize(
  storageKey: string,
  defaults: Record<string, number>,
  visibleIds: string[],
): {
  gridRef: React.RefObject<HTMLDivElement>;
  gridTemplate: string;
  vars: React.CSSProperties;
  startResize: (id: string, e: React.MouseEvent) => void;
} {
  const gridRef = useRef<HTMLDivElement>(null);
  const [frs, setFrs] = useState<Record<string, number>>(() => load(storageKey, defaults));
  const detachRef = useRef<(() => void) | null>(null);
  useEffect(() => () => detachRef.current?.(), []);

  // Без мемоизации намеренно: `gridTemplate` — join по ≤15 строкам, а идентичность `vars` не
  // несёт нагрузки (React сравнивает style по значению, и переменные, выставленные императивно
  // мид-драга, переживают ре-рендер в любом случае). Мемо тут требовало бы искусственного
  // строкового ключа и двух eslint-disable — дороже, чем то, что оно экономит.
  const frOf = (id: string): number => frs[id] ?? defaults[id] ?? 1;
  const gridTemplate = visibleIds.map((id) => `var(--col-${id})`).join(" ");
  const vars = Object.fromEntries(
    visibleIds.map((id) => [`--col-${id}`, `${frOf(id)}fr`]),
  ) as React.CSSProperties;

  const startResize = (id: string, e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    const el = gridRef.current;
    const i = visibleIds.indexOf(id);
    const nextId = visibleIds[i + 1];
    if (!el || i < 0 || !nextId) return; // последнюю колонку тянуть не за что
    const startX = e.clientX;
    const total = visibleIds.reduce((s, c) => s + frOf(c), 0);
    const pxPerFr = el.clientWidth / total;
    const a0 = frOf(id);
    const b0 = frOf(nextId);
    let a = a0;
    let b = b0;
    const onMove = (ev: MouseEvent): void => {
      const d = Math.max(MIN_FR - a0, Math.min(b0 - MIN_FR, (ev.clientX - startX) / pxPerFr));
      a = a0 + d;
      b = b0 - d;
      el.style.setProperty(`--col-${id}`, `${a}fr`); // ← в DOM, без setState
      el.style.setProperty(`--col-${nextId}`, `${b}fr`);
    };
    const detach = (): void => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      detachRef.current = null;
    };
    const onUp = (): void => {
      detach();
      const next = { ...frs, [id]: a, [nextId]: b };
      setFrs(next); // коммит → следующий рендер применит те же числа (без прыжка)
      localStorage.setItem(storageKey, JSON.stringify(next));
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    detachRef.current = detach;
  };

  return { gridRef, gridTemplate, vars, startResize };
}
